import React, { createContext, useContext, useEffect, useState } from 'react';
import { clearRefresh, api, AuthProviders, LoginResult, TotpEnroll, getToken, setToken, User } from './api';

/** A pre-MFA session awaiting the TOTP step — held in memory only, never persisted. */
type Pending = LoginResult;

/** One-shot auth state derived from the OAuth/verify/reset redirect query params. */
type InitialAuth = {
  pending: Pending | null; // from ?otc= (exchanged for a pre-MFA session)
  resetToken: string; // from ?reset=
  notice: string; // from ?verified=1 (success banner)
  error: string; // from ?login_error= (mapped, error banner)
};

type AuthCtx = {
  user: User | null;
  loading: boolean;
  initial: InitialAuth;
  loginWithToken: (token: string, user?: User) => Promise<void>;
  patchUser: (u: User) => void;
  logout: () => void;
};

const Ctx = createContext<AuthCtx>(null as any);
export const useAuth = () => useContext(Ctx);

// Redirect ?login_error= codes → human-readable copy (AC-5); unknown/generic below.
const LOGIN_ERRORS: Record<string, string> = {
  personal_account_not_allowed:
    "Personal Microsoft accounts aren't allowed — use your work or school account.",
  email_not_verified: "Your Google email isn't verified.",
  verify_expired: 'That verification link expired — sign in to resend / reset.',
};

export function ChatAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initial, setInitial] = useState<InitialAuth>({
    pending: null,
    resetToken: '',
    notice: '',
    error: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const otc = params.get('otc');
    const reset = params.get('reset') || '';
    const verified = params.get('verified');
    const loginErr = params.get('login_error');
    // Consume the one-shot params so a refresh can't re-trigger them.
    if (otc || reset || verified || loginErr) {
      ['otc', 'reset', 'verified', 'login_error'].forEach((k) => params.delete(k));
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (params.toString() ? `?${params}` : ''),
      );
    }
    const base: InitialAuth = {
      pending: null,
      resetToken: reset,
      notice: verified ? 'Your email is verified — please sign in.' : '',
      error: loginErr ? LOGIN_ERRORS[loginErr] || 'Sign-in failed, please try again.' : '',
    };

    // OAuth handover: redeem the single-use code for a pre-MFA session (memory only).
    if (otc) {
      api
        .exchange(otc)
        .then((p) => setInitial({ ...base, pending: p }))
        .catch(() => setInitial({ ...base, error: 'That sign-in link expired — please sign in again.' }))
        .finally(() => setLoading(false));
      return;
    }
    setInitial(base);

    // A reset link takes precedence over resuming a stored session.
    if (reset || !getToken()) {
      setLoading(false);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const loginWithToken = async (token: string, u?: User) => {
    setToken(token);
    setUser(u ?? (await api.me()));
  };
  const patchUser = (u: User) => setUser(u);
  const logout = () => {
    setToken(null);
    clearRefresh();  // explicit sign-out forgets the device; expiry/refresh failures do NOT
    setUser(null);
  };

  // Apply the user's theme to the app root (dark default, light override).
  useEffect(() => {
    const light = user?.theme === 'light';
    document.documentElement.classList.toggle('fx-theme-light', light);
    return () => document.documentElement.classList.remove('fx-theme-light');
  }, [user?.theme]);

  return (
    <Ctx.Provider value={{ user, loading, initial, loginWithToken, patchUser, logout }}>
      {children}
    </Ctx.Provider>
  );
}

type Stage =
  | 'credentials'
  | 'signup'
  | 'check-email'
  | 'forgot'
  | 'reset'
  | 'totp-enroll'
  | 'totp'
  | 'recovery';

const TITLES: Record<Stage, string> = {
  credentials: 'Sign in to Helm',
  signup: 'Create your account',
  'check-email': 'Check your email',
  forgot: 'Reset your password',
  reset: 'Choose a new password',
  'totp-enroll': 'Set up two-factor',
  totp: 'Two-factor authentication',
  recovery: 'Use a recovery code',
};

// Server `detail` codes → friendly copy; unknown codes fall back to the raw detail.
const AUTH_MESSAGES: Record<string, string> = {
  invalid_credentials: 'Incorrect email or password.',
  email_not_verified: 'Verify your email first — check your inbox, or reset your password to verify.',
  email_already_registered: 'That email is already registered — sign in instead.',
  email_send_failed: "We couldn't send that email — please try again.",
  invalid_or_expired_token: 'That reset link is invalid or has expired.',
  invalid_or_expired_code: 'That sign-in link expired — please sign in again.',
  invalid_code: 'That code is incorrect.',
  code_already_used: 'That code was already used — wait for the next one.',
  totp_not_enrolled: 'Enrollment expired — please start again.',
  totp_not_activated: "Two-factor isn't set up — sign in again.",
  totp_already_activated: 'Two-factor is already set up — sign in instead.',
};
const msg = (d?: string) => (d && AUTH_MESSAGES[d]) || d || 'Something went wrong.';

// segno emits <svg width="N" height="N"> with no viewBox, so CSS can't scale it up.
// Swap the fixed size for a viewBox so the QR fills a scannable box.
const qrWithViewBox = (svg: string): string =>
  svg.replace(/^<svg width="(\d+)" height="(\d+)"/, '<svg viewBox="0 0 $1 $2"');

// Which screen to open on first render, from the redirect params.
function initialStage(i: InitialAuth): Stage {
  if (i.resetToken) return 'reset';
  if (i.pending) return i.pending.stage === 'totp_enroll' ? 'totp-enroll' : 'totp';
  return 'credentials';
}

/** Real auth: email+password / Google / Microsoft, then a mandatory TOTP factor. */
export function LoginScreen({ startStage, onHome }: { startStage?: Stage; onHome?: () => void } = {}) {
  const { loginWithToken, initial } = useAuth();
  // A redirect param (reset/otc) always wins; otherwise honor the requested start stage.
  const [stage, setStage] = useState<Stage>(() => {
    const fromParams = initialStage(initial);
    return fromParams === 'credentials' && startStage ? startStage : fromParams;
  });
  const [pending, setPending] = useState<Pending | null>(initial.pending);
  const [providers, setProviders] = useState<AuthProviders | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enroll, setEnroll] = useState<TotpEnroll | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [earnedToken, setEarnedToken] = useState('');
  const [ack, setAck] = useState(false);
  const [note, setNote] = useState(initial.notice);
  const [err, setErr] = useState(initial.error);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.authProviders().then(setProviders).catch(() => setProviders({ google: false, microsoft: false }));
  }, []);

  // Fetch the enrollment secret + QR once we reach the enroll screen.
  useEffect(() => {
    if (stage === 'totp-enroll' && pending && !enroll && !recoveryCodes) {
      api.totpEnroll(pending.token).then(setEnroll).catch((e) => setErr(msg(e?.message)));
    }
  }, [stage, pending, enroll, recoveryCodes]);

  const go = (s: Stage) => {
    setErr('');
    setNote('');
    setCode('');
    setStage(s);
  };

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    setNote('');
    try {
      const res = await api.login(email.trim(), password);
      setPending(res);
      setCode('');
      setStage(res.stage === 'totp_enroll' ? 'totp-enroll' : 'totp');
    } catch (ex: any) {
      setErr(msg(ex?.message));
    } finally {
      setBusy(false);
    }
  };

  const doSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await api.signup(email.trim(), password);
      setStage('check-email');
    } catch (ex: any) {
      setErr(msg(ex?.message));
      if (ex?.message === 'email_already_registered') setStage('credentials');
    } finally {
      setBusy(false);
    }
  };

  const doForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await api.resetRequest(email.trim());
    } catch {
      // 204-always endpoint — nothing to surface.
    }
    setNote('If that email exists, we sent a reset link.');
    setBusy(false);
  };

  const doReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await api.resetConfirm(initial.resetToken, password);
      setPassword('');
      setNote('Password updated — please sign in.');
      setStage('credentials');
    } catch (ex: any) {
      setErr(msg(ex?.message));
    } finally {
      setBusy(false);
    }
  };

  const doActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pending) return;
    setBusy(true);
    setErr('');
    try {
      const res = await api.totpActivate(pending.token, code.trim());
      setEarnedToken(res.token);
      setRecoveryCodes(res.recovery_codes);
    } catch (ex: any) {
      setErr(msg(ex?.message));
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pending) return;
    setBusy(true);
    setErr('');
    try {
      const res = await api.totpVerify(pending.token, code.trim());
      await loginWithToken(res.token, pending.user);
    } catch (ex: any) {
      setErr(msg(ex?.message));
      setBusy(false);
    }
  };

  const doRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pending) return;
    setBusy(true);
    setErr('');
    try {
      const res = await api.recovery(pending.token, code.trim());
      await loginWithToken(res.token, pending.user);
    } catch (ex: any) {
      setErr(msg(ex?.message));
      setBusy(false);
    }
  };

  const errBox = err ? <div className="fx-login-err">{err}</div> : null;
  const emailField = (
    <input
      className="fx-text-input fx-login-input"
      type="email"
      placeholder="you@company.com"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      autoComplete="email"
      required
      autoFocus
    />
  );
  const codeField = (
    <input
      className="fx-text-input fx-login-input"
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="123456"
      value={code}
      onChange={(e) => setCode(e.target.value)}
      autoFocus
    />
  );

  const renderStage = () => {
    switch (stage) {
      case 'signup':
        return (
          <form className="fx-login-form" onSubmit={doSignup}>
            {emailField}
            <input
              className="fx-text-input fx-login-input"
              type="password"
              placeholder="Password (10+ characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={10}
              required
            />
            {errBox}
            <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy}>
              {busy ? 'Creating…' : 'Create account'}
            </button>
            <button type="button" className="fx-login-back" onClick={() => go('credentials')}>
              ← Back to sign in
            </button>
          </form>
        );

      case 'check-email':
        return (
          <>
            <div className="fx-login-note">
              We sent a verification link to <b>{email}</b>. Click it to activate your account,
              then sign in.
            </div>
            <button type="button" className="fx-sendbtn fx-login-btn" onClick={() => go('credentials')}>
              Back to sign in
            </button>
          </>
        );

      case 'forgot':
        return note ? (
          <>
            <div className="fx-login-note">{note}</div>
            <button type="button" className="fx-sendbtn fx-login-btn" onClick={() => go('credentials')}>
              Back to sign in
            </button>
          </>
        ) : (
          <form className="fx-login-form" onSubmit={doForgot}>
            {emailField}
            {errBox}
            <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <button type="button" className="fx-login-back" onClick={() => go('credentials')}>
              ← Back to sign in
            </button>
          </form>
        );

      case 'reset':
        return (
          <form className="fx-login-form" onSubmit={doReset}>
            <input
              className="fx-text-input fx-login-input"
              type="password"
              placeholder="New password (10+ characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={10}
              required
              autoFocus
            />
            {errBox}
            <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
            <button type="button" className="fx-login-back" onClick={() => go('credentials')}>
              ← Back to sign in
            </button>
          </form>
        );

      case 'totp-enroll':
        return recoveryCodes ? (
          <>
            <div className="fx-login-note">
              Save these 8 recovery codes somewhere safe — each works once if you lose your
              authenticator. They won’t be shown again.
            </div>
            <div className="fx-recovery-codes">
              {recoveryCodes.map((c) => (
                <code key={c}>{c}</code>
              ))}
            </div>
            <label className="fx-check-row">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              I’ve saved my recovery codes
            </label>
            <button
              type="button"
              className="fx-sendbtn fx-login-btn"
              disabled={!ack}
              onClick={() => {
                if (pending) loginWithToken(earnedToken, pending.user);
              }}
            >
              Continue
            </button>
          </>
        ) : (
          <>
            <div className="fx-login-note">
              Scan this QR code with an authenticator app (or enter the key), then enter the
              6-digit code to finish.
            </div>
            {enroll ? (
              <>
                <div
                  className="fx-totp-qr"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: qrWithViewBox(enroll.qr_svg) }}
                />
                <div className="fx-totp-secret">{enroll.secret}</div>
              </>
            ) : (
              <div className="fx-login-note">Loading…</div>
            )}
            <form className="fx-login-form" onSubmit={doActivate}>
              {codeField}
              {errBox}
              <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy}>
                {busy ? 'Verifying…' : 'Activate'}
              </button>
            </form>
          </>
        );

      case 'totp':
        return (
          <form className="fx-login-form" onSubmit={doVerify}>
            <div className="fx-login-note">Enter the 6-digit code from your authenticator app.</div>
            {codeField}
            {errBox}
            <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" className="fx-login-back" onClick={() => go('recovery')}>
              Use a recovery code
            </button>
          </form>
        );

      case 'recovery':
        return (
          <form className="fx-login-form" onSubmit={doRecovery}>
            <div className="fx-login-note">Enter one of your recovery codes.</div>
            <input
              className="fx-text-input fx-login-input"
              type="text"
              autoComplete="one-time-code"
              placeholder="xxxxx-xxxxx"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
            {errBox}
            <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" className="fx-login-back" onClick={() => go('totp')}>
              ← Back
            </button>
          </form>
        );

      default: // 'credentials'
        return (
          <>
            {providers?.google && (
              <a className="fx-oauth-btn" href={api.oauthStartUrl('google')}>
                <span className="fx-oauth-ic">
                  <svg width="15" height="15" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                  </svg>
                </span> Continue with Google
              </a>
            )}
            {providers?.microsoft && (
              <a className="fx-oauth-btn" href={api.oauthStartUrl('microsoft')}>
                <span className="fx-oauth-ic">
                  <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                </span> Continue with Microsoft
              </a>
            )}
            {(providers?.google || providers?.microsoft) && <div className="fx-login-or">or</div>}
            <form className="fx-login-form" onSubmit={doLogin}>
              {emailField}
              <input
                className="fx-text-input fx-login-input"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              {note && <div className="fx-login-note">{note}</div>}
              {errBox}
              <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            <div className="fx-login-links">
              <button type="button" className="fx-login-back" onClick={() => go('signup')}>
                Create account
              </button>
              <button type="button" className="fx-login-back" onClick={() => go('forgot')}>
                Forgot password?
              </button>
            </div>
          </>
        );
    }
  };

  const canGoHome = onHome && (stage === 'credentials' || stage === 'signup' || stage === 'forgot');
  return (
    <div className="fx-login">
      <div className="fx-login-card">
        {canGoHome && (
          <button type="button" className="fx-login-home" onClick={onHome}>← Home</button>
        )}
        <div className="fx-login-logo">F</div>
        <div className="fx-login-title">{TITLES[stage]}</div>
        {renderStage()}
      </div>
    </div>
  );
}
